/**
 * shared/onboarding.ts - the onboarding chat's state and the rules the server and the client share (2026-10-08).
 *
 * The state lives in workspaces.onboarding (JSONB, nullable, added at startup by server/onboarding.ts ensureOnboardingSchema):
 *   null                         a workspace made before the marker existed: finished, unless it is still the bare
 *                                "My Business" placeholder an abandoned onboarding chat left behind
 *   { status: "in_progress" }    the chat started; the app shows the chat again on reload until it completes
 *   { status: "complete" }       finished; completedAt says when, followUps what ran in the background afterwards
 *
 * Pure functions only (no I/O), so the resume gate can be tested on its own (scripts/regression/onboarding.mts).
 */

export const ONBOARDING_GOALS = {
  leads: { label: "More enquiries and booked calls", nurtureGoal: "appointment" },
  sales: { label: "More sales", nurtureGoal: "appointment" },
  awareness: { label: "Get known in our market", nurtureGoal: "awareness" },
} as const;
export type OnboardingGoal = keyof typeof ONBOARDING_GOALS;

/** The channels the chat offers. Google is not offered (not supported yet). "blog" = the business's own website blog. */
export const ONBOARDING_CHANNELS = ["linkedin", "meta", "x", "blog"] as const;
export type OnboardingChannel = (typeof ONBOARDING_CHANNELS)[number];
export const ONBOARDING_CHANNEL_LABELS: Record<OnboardingChannel, string> = {
  linkedin: "LinkedIn",
  meta: "Instagram / Facebook",
  x: "X",
  blog: "Website blog",
};
/** The social channels among them (each has a connect link). */
export const SOCIAL_ONBOARDING_CHANNELS: readonly OnboardingChannel[] = ["linkedin", "meta", "x"];

export interface OnboardingAnswers {
  goal?: OnboardingGoal;
  /** the owner's own words about the goal, when they added detail */
  goalNote?: string;
  /** names or websites as the owner wrote them; [] = they had none or skipped */
  competitors?: string[];
  /** [] = none for now */
  channels?: OnboardingChannel[];
  challenge?: string;
  /** what they most want to sell (services / products), in their words; every post supports these */
  offers?: string[];
  /** things Draper must never say, promise or post about; [] = nothing */
  avoid?: string[];
  /** how customers reach the business, in the owner's words ("" = skipped); phone / email / address also go to contactInfo */
  contact?: string;
  /** who signs the articles as reviewer ("Dr. Priya Shah, DDS"); "" = sign with the business name */
  byline?: string;
}

/** Phone, email and address in the owner's answer to "how should customers reach you?". */
export function parseContact(reply: string): { phone?: string; email?: string; address?: string } {
  const r = String(reply || "");
  const phone = /(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/.exec(r)?.[0]?.trim();
  const email = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/.exec(r)?.[0];
  // an address: a street number and a street word ("12 Lakeshore Rd W", "450 King St, Unit 3")
  const address = /\b\d{1,6}\s+[A-Z][\w'.-]*(?:\s+[A-Z][\w'.-]*)*\s+(?:St(?:reet)?|Rd|Road|Ave(?:nue)?|Blvd|Boulevard|Dr(?:ive)?|Way|Lane|Ln|Cres(?:cent)?|Crt|Court|Pl(?:ace)?|Pkwy|Hwy|Line)\b[^.;\n]{0,40}/.exec(r)?.[0]?.replace(/[\s,]+$/, "");
  return { ...(phone ? { phone } : {}), ...(email ? { email } : {}), ...(address ? { address } : {}) };
}

export type OnboardingFollowUpKey = "brand" | "knowledge" | "strategy" | "harry";
export interface OnboardingFollowUpResult {
  status: "done" | "skipped" | "failed";
  at: string;
  detail?: string;
}

export interface OnboardingState {
  status: "in_progress" | "complete";
  startedAt?: string;
  completedAt?: string;
  answers?: OnboardingAnswers;
  followUps?: Partial<Record<OnboardingFollowUpKey, OnboardingFollowUpResult>>;
}

type WsLike = { name?: string | null; brandName?: string | null; userId?: string | null; onboarding?: unknown };

export function isPlaceholderName(v: unknown): boolean {
  const s = typeof v === "string" ? v.trim().toLowerCase() : "";
  return s === "" || s === "my business";
}

export function onboardingStateOf(ws: { onboarding?: unknown } | null | undefined): OnboardingState | null {
  const st = ws?.onboarding;
  if (!st || typeof st !== "object" || Array.isArray(st)) return null;
  return st as OnboardingState;
}

/**
 * Has this workspace finished onboarding? With a marker: its status. Without one (made before the marker existed): finished,
 * unless it is still the bare "My Business" placeholder with no real brand name (an onboarding chat abandoned half-way).
 */
export function isOnboardingComplete(ws: WsLike): boolean {
  const st = onboardingStateOf(ws);
  if (st) return st.status === "complete" || !!st.completedAt;
  return !(isPlaceholderName(ws.name) && isPlaceholderName(ws.brandName));
}

/**
 * Whether the signed-in user sees the onboarding chat instead of the app: no workspace at all, or their only workspace is
 * their own and has not finished onboarding (a reload mid-chat resumes the chat). A workspace someone else owns (an invited
 * team member) or a user with several workspaces never sees it.
 */
export function shouldShowOnboarding(workspaces: WsLike[] | null | undefined, userId: string | null | undefined): boolean {
  if (!Array.isArray(workspaces)) return false;
  if (workspaces.length === 0) return true;
  if (workspaces.length !== 1) return false;
  const only = workspaces[0];
  if (userId && only.userId && only.userId !== userId) return false;
  return !isOnboardingComplete(only);
}

/** "leads" / "sales" / "awareness", or the owner's words for one of them. Anything else: null. */
export function normalizeGoal(v: unknown): OnboardingGoal | null {
  const s = typeof v === "string" ? v.trim().toLowerCase() : "";
  if (!s) return null;
  if (s in ONBOARDING_GOALS) return s as OnboardingGoal;
  if (/\b(enquir|inquir|lead|call|booking|booked|appointment|demo|consult|meeting|sign-?ups?|trials?)\w*/.test(s)) return "leads";
  if (/\b(sale|sell|revenue|order|purchase|customers? buy)\w*/.test(s)) return "sales";
  if (/\b(known|awareness|brand|visib|reach|audience|followers?|recogni)\w*/.test(s)) return "awareness";
  return null;
}

const CHANNEL_ALIASES: Record<string, OnboardingChannel> = {
  linkedin: "linkedin", "linked in": "linkedin",
  meta: "meta", instagram: "meta", facebook: "meta", ig: "meta", fb: "meta", "instagram/facebook": "meta", "instagram / facebook": "meta",
  x: "x", twitter: "x", "x (twitter)": "x",
  blog: "blog", website: "blog", "website blog": "blog", wordpress: "blog", "our blog": "blog", "my blog": "blog",
};

/** The chosen channels in a fixed order, deduplicated. Google and anything unknown are dropped. Accepts a list or "a, b and c". */
export function normalizeChannels(v: unknown): OnboardingChannel[] {
  const raw: string[] = Array.isArray(v) ? v.map((x) => String(x ?? "")) : typeof v === "string" ? v.split(/,|;|\band\b|\n|\//i) : [];
  const out = new Set<OnboardingChannel>();
  for (const r of raw) {
    const k = r.trim().toLowerCase().replace(/\s+/g, " ");
    if (!k) continue;
    const hit = CHANNEL_ALIASES[k] ?? (Object.entries(CHANNEL_ALIASES).find(([alias]) => alias.length > 2 && k.includes(alias))?.[1]);
    if (hit) out.add(hit);
  }
  return ONBOARDING_CHANNELS.filter((c) => out.has(c));
}

/** workspaces.channel_preferences for the chosen channels: Google always off; the blog choice is kept next to the social switches. */
export function channelPreferencesFor(channels: readonly OnboardingChannel[]): { linkedin: boolean; x: boolean; meta: boolean; google: boolean; blog: boolean } {
  const has = (c: OnboardingChannel) => channels.includes(c);
  return { linkedin: has("linkedin"), x: has("x"), meta: has("meta"), google: false, blog: has("blog") };
}

const NONE_RE = /^(none|no|nope|n\/a|na|skip|not sure|no idea|nobody|no one|don'?t know|idk|-)\.?$/i;

/** Competitor names / sites as the owner wrote them: trimmed, "none"/"skip" dropped, deduplicated (case-insensitive), at most 10. */
export function normalizeCompetitors(v: unknown): string[] {
  const raw: string[] = Array.isArray(v) ? v.map((x) => String(x ?? "")) : typeof v === "string" ? v.split(/,|;|\n/) : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of raw) {
    const s = r.replace(/\s+/g, " ").trim().replace(/^(and|or)\s+/i, "").replace(/[.!?]+$/, "").slice(0, 120);
    if (!s || NONE_RE.test(s)) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
    if (out.length >= 10) break;
  }
  return out;
}

/** What the chat still has to ask, in order. "business" = the name or the industry is not saved yet. */
export function remainingOnboardingSteps(ws: WsLike & { industry?: string | null }): Array<"business" | "goal" | "competitors" | "channels"> {
  const a = onboardingStateOf(ws)?.answers ?? {};
  const out: Array<"business" | "goal" | "competitors" | "channels"> = [];
  if (isPlaceholderName(ws.brandName) || !String(ws.industry ?? "").trim()) out.push("business");
  if (!a.goal) out.push("goal");
  if (a.competitors === undefined) out.push("competitors");
  if (a.channels === undefined) out.push("channels");
  return out;
}

/** Whether a social channel is connected: the workspace's integrations flag (same rule as the content studio) or a stored OAuth token. */
export function channelConnected(integrations: unknown, tokenPlatforms: readonly string[], channel: OnboardingChannel): boolean {
  const integ = (integrations && typeof integrations === "object" ? integrations : {}) as Record<string, { connected?: boolean } | undefined>;
  const tok = new Set(tokenPlatforms.map((p) => String(p).toLowerCase()));
  if (channel === "meta") return !!(integ.meta?.connected || integ.instagram?.connected || integ.facebook?.connected || integ.instagram_business?.connected)
    || ["meta", "facebook", "instagram", "instagram_business"].some((p) => tok.has(p));
  if (channel === "x") return !!integ.x?.connected || tok.has("x") || tok.has("twitter");
  if (channel === "linkedin") return !!integ.linkedin?.connected || tok.has("linkedin");
  return false;
}

/** One line of the "Finish setup" card on Draper home (GET /api/workspaces/:wsId/setup-status). */
export interface SetupItem {
  key: string;
  label: string;
  done: boolean;
  /** the exact place in the app where it is done */
  href: string;
  detail?: string;
}

/** A short list from the owner's words ("cleanings, whitening and implants"): split on commas / "and" / new lines, filler dropped, at most 8. */
export function normalizeList(v: unknown, max = 8): string[] {
  const raw: string[] = Array.isArray(v) ? v.map((x) => String(x ?? "")) : typeof v === "string" ? v.split(/,|;|\n|\band\b|\bor\b/i) : [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of raw) {
    const s = r.replace(/\s+/g, " ").trim()
      .replace(/^(?:we |i |our |mainly |mostly |just |only |also |and |never |don'?t |do not |please )+/i, "")
      .replace(/[\s.!–—-]+$/, "").slice(0, 120);
    if (!s || NONE_RE.test(s) || s.length < 2) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
    if (out.length >= max) break;
  }
  return out;
}

export type OnboardingQuestion = "goal" | "competitors" | "channels" | "offers" | "avoid" | "offers_and_avoid" | "audience" | "contact" | "byline";

/** Which setup question Draper's last message asked (the chat asks one question per message), or null. */
export function questionAsked(draperText: string): OnboardingQuestion | null {
  // the QUESTION itself: the last sentence that ends in "?", formatting stripped (run 5: "I've added that to the list of things
  // to avoid. Now, where would you like to show up…?" was read as the never-say question, and "*never* say" was missed)
  // abbreviation dots ("e.g.", "Dr.") are not sentence ends: "whose name should sign …, e.g. Dr. Priya Shah?" is one question
  const plain = String(draperText || "").replace(/[*_`]/g, "").replace(/\s+/g, " ").toLowerCase()
    .replace(/\b(e\.g|i\.e|etc|dr|mr|mrs|ms|st|ave|rd)\./g, (m) => m.replace(/\./g, ""));
  const questions = plain.match(/[^.!?]*\?/g);
  const last = questions && questions.length ? questions.slice(-2).join(" ").slice(-400) : plain.slice(-400);
  const asksAvoid = /(never (say|promise|mention|post)|avoid|off[- ]limits|stay away from)/.test(last);
  const asksOffers = /(what do you (most )?want to sell|main (services|products|offers)|what do you sell|which services|services or products)/.test(last);
  // the two hybrid-launch questions (2026-11-03): how customers reach them, and who signs the articles
  if (/(whose name|name (?:should|to) (?:go|appear|sign)|sign (?:your|the|off on) (?:articles|blog)|reviewed by|byline|reviews? (?:your|the) articles)/.test(last)) return "byline";
  if (/(how (?:should|do|can|would) (?:customers|clients|patients|guests|people|homeowners|they) (?:reach|contact|get (?:in touch|hold))|phone number|best (?:number|way to reach|way for)|reach you)/.test(last)) return "contact";
  // both in one message (2026-11-02: a law firm's services were saved as its never-say list): the reply is split
  if (asksAvoid && asksOffers) return "offers_and_avoid";
  if (/(ideal (customer|client)|who (?:is|are) your (?:ideal |typical |main )?(?:customers?|clients?|patients?)|who do you (?:most )?(?:serve|want to reach))/.test(last) && !asksAvoid && !asksOffers) return "audience";
  if (asksAvoid) return "avoid";
  if (asksOffers) return "offers";
  if (/(show up|which channels|where (?:do|would|should) you (?:want|like)[^?]*(?:online|post|appear|be seen)|linkedin[\s\S]*(instagram|blog)|(instagram|blog)[\s\S]*linkedin)/.test(last)) return "channels";
  if (/competitor/.test(last)) return "competitors";
  if (/(90 days|one goal|main goal|goal for)/.test(last)) return "goal";
  return null;
}

/**
 * The owner's answer to a setup question, read without the model (2026-11-02: Draper said it saved the competitors and the
 * "blog only" choice but never called the tool). Returns only what the reply clearly says; null when it says nothing usable.
 */
export function parseOnboardingReply(question: OnboardingQuestion, reply: string): Partial<OnboardingAnswers> | null {
  const r = String(reply || "").trim();
  if (!r) return null;
  const none = NONE_RE.test(r) || /^(none|no|nothing|skip|not (yet|now|really))\b/i.test(r);
  switch (question) {
    case "goal": {
      const g = normalizeGoal(r);
      return g ? { goal: g, goalNote: r.slice(0, 200) } : null;
    }
    case "competitors": {
      if (none) return { competitors: [] };
      const parts = r.split(/,|;|\n|\band\b/i).map((x) => x.replace(/^\s*(i (watch|follow|keep an eye on)|mainly|mostly|probably|maybe)\s+/i, ""));
      const list = normalizeCompetitors(parts);
      return list.length ? { competitors: list } : null;
    }
    case "channels": {
      if (none) return { channels: [] };
      // the channels the owner turned DOWN are not choices ("Instagram/Facebook and the blog. Not LinkedIn or X.")
      const low = r.toLowerCase().replace(/\b(?:not|no|except|without|but not|skip|never)\b[^.;!\n]*/g, " ");
      if (/\b(all of them|all|everything)\b/i.test(low)) return { channels: [...ONBOARDING_CHANNELS] };
      const found = new Set<OnboardingChannel>();
      if (/linked\s?in/.test(low)) found.add("linkedin");
      if (/\b(instagram|facebook|meta|ig|fb)\b/.test(low)) found.add("meta");
      if (/\b(x|twitter)\b/.test(low)) found.add("x");
      if (/\b(blog|journal|articles?|website)\b/.test(low)) found.add("blog");
      return found.size ? { channels: ONBOARDING_CHANNELS.filter((c) => found.has(c)) } : null;
    }
    case "offers": {
      const list = normalizeList(r);
      return list.length ? { offers: list } : null;
    }
    case "avoid": {
      if (none) return { avoid: [] };
      const list = normalizeList(r);
      return list.length ? { avoid: list } : null;
    }
    case "audience": {
      // saved as the workspace's target audience by the caller (an answer, not a list)
      // a bare confirmation is not an answer (run 4 saved "Yes, that's right." as a CPA's ideal customer)
      const confirmation = /^(?:yes|yeah|yep|yup|correct|right|exactly|sure|ok(?:ay)?|that'?s (?:right|correct|it))\b[\s,.!]*(?:that'?s (?:right|correct|it)[.!]?)?$/i.test(r);
      return r.length >= 8 && !none && !confirmation ? { audience: r.replace(/\s+/g, " ").slice(0, 300) } as any : null;
    }
    case "contact": {
      const c = parseContact(r);
      if (Object.keys(c).length) return { contact: r.replace(/\s+/g, " ").slice(0, 300), ...c } as any;
      return none ? { contact: "" } : null;
    }
    case "byline": {
      if (none || /\b(?:just )?(?:the )?(?:business|company|clinic|firm|practice|team)(?:'s)? name\b|\bthe team\b/i.test(r)) return { byline: "" };
      const name = r.replace(/^\s*(?:it'?s|use|put|sign (?:them|it) (?:as|with)|reviewed by|written by|by)\s+/i, "").replace(/[.!]+$/, "").replace(/\s+/g, " ").trim();
      // a person's name: at least two capitalised words ("Dr. Priya Shah, DDS", "Mark Reid")
      return /[A-Z][\w.'-]*\s+[A-Z][\w'-]+/.test(name) && name.length <= 100 ? { byline: name } : null;
    }
    case "offers_and_avoid": {
      // "We do closings, wills and estates. Never say guaranteed or best lawyers." -> offers before the never/don't part, avoid after
      const m = /(?:^|[.;\n]|\bbut\b|\band\b)\s*(?:please\s+)?(?:never|don'?t|do not|avoid|no)\b/i.exec(r);
      if (!m) {
        const list = normalizeList(r);
        return list.length ? { offers: list } : null;
      }
      const offers = normalizeList(r.slice(0, m.index));
      const avoidText = r.slice(m.index).replace(/^[\s.;,]*(?:but|and)?\s*(?:please\s+)?(?:never|don'?t|do not|avoid|no)\s+(?:say|mention|use|post about|promise|talk about)?\s*/i, "");
      const avoid = normalizeList(avoidText.replace(/["“”]/g, ""));
      const out: Partial<OnboardingAnswers> = {};
      if (offers.length) out.offers = offers;
      if (avoid.length) out.avoid = avoid;
      return Object.keys(out).length ? out : null;
    }
  }
}

/**
 * The key phrase of a never-say item ("anything about gluten-free (they are not)" -> "gluten-free"; "say painless" ->
 * "painless"), so posts can be checked against it; "" when the item is too vague to match literally.
 */
export function neverSayPhrase(item: string): string {
  const raw = String(item || "");
  // a quoted phrase is the phrase (`say "maximum refund guaranteed"`); an explanation after a dash or colon is not (`24/7 - we are not…`)
  const quoted = /["“]([^"”]{2,60})["”]/.exec(raw);
  const head = quoted ? quoted[1] : raw.split(/\s[-–—]\s|\s[-–—]$|:|\(/)[0];
  const s = head.toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/["“”']/g, " ")
    .replace(/^\s*(?:anything|something|nothing)?\s*(?:about|regarding|on|related to)?\s*/, "")
    .replace(/^\s*(?:never\s+)?(?:say|saying|mention|mentioning|use|using|post about|promise|promising|talk about|claim|claiming|call us|calling us)\s+/, "")
    // "say anything about gluten-free" (run 7: the check looked for the words "anything about gluten-free")
    .replace(/^\s*(?:anything|something)\s+(?:about|regarding|on|related to)\s+/, "")
    .replace(/^\s*(?:the word|the words|words like|that we(?:'re| are)?|we(?:'re| are))\s+/, "")
    .replace(/\s+/g, " ").trim();
  const words = s.split(" ").filter(Boolean);
  return words.length >= 1 && words.length <= 4 && s.length >= 3 ? s : "";
}
